import AntForm from "antd/es/form";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useCaptcha } from "utils/hooks/useCaptcha";
import Input from "common/Inputs/Input";
import {
  LoginRequest,
  LoginResponse,
  LoginResponseWithUserId,
  LanguageNameSpaces,
  OAuthUserRequest,
} from "@shared/types";
import {
  IS_WEB_VIEW,
  LARGE_MOBILE,
  LOCAL_STORAGE_REMEMBER_ME_KEY,
  MOBILE_SEARCH,
  RESET_PASSWORD_PATH,
} from "common/constants";
import { PrimaryTitle } from "features/auth/styles";
import styled, { useTheme } from "styled-components";
import Link from "common/Typography/Link.tsx";
import useMedia from "utils/hooks/useMedia";
import {
  EMAIL_INPUT_PROPS,
  getEmailFormItemRules,
} from "features/auth/constants";
import AuthFormFooter from "features/auth/AuthFormFooter";
import AuthInlineError, {
  useAuthResultMessage,
} from "features/auth/AuthInlineError";
import useQuery from "utils/hooks/useQuery";
import { sharedLib } from "@shared/services/shared.service";
import { handleUserTempOAuthData } from "@shared/features/onboarding/utils";
import SocialLogins from "features/auth/SocialLogins";
import { getUtmUserUuid } from "@shared/utils/misc";
import { MobileLoginData } from "@shared/services/webview/types.ts";
import {
  requestBiometricLogin,
  triggerClearBiometric,
  triggerSaveBiometric,
} from "services/webview/webViewBridgeUtils";
import Form from "common/Form/Form.tsx";
import Flex from "common/Boxes/Flex.tsx";
import Text from "common/Typography/Text.tsx";
import Checkbox from "common/Checkbox/Checkbox";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery.ts";
import { selectMobileDeviceData } from "@shared/slices/common.slice.ts";
import { useDispatch, useSelector } from "react-redux";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";
import LoginDescription from "features/configuration/InviteExternalUsers/LoginDescription.tsx";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  AUTH_FORGOT_PASSWORD_LINK_ID,
  AUTH_LOGIN_EMAIL_INPUT_ID,
  AUTH_LOGIN_PASSWORD_INPUT_ID,
} from "testIds.ts";

interface LoginFormProps {
  onSuccessfulLogin: (args: LoginRequest | OAuthUserRequest) => void;
  setLoginResponse: (response: LoginResponse) => void;
  setIsBiometricLogin: (isBiometricLogin: boolean) => void;
  onFinalizedLogin: () => void;
}

const LoginForm = ({
  onSuccessfulLogin,
  setLoginResponse,
  setIsBiometricLogin,
  onFinalizedLogin,
}: LoginFormProps) => {
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const [form] = AntForm.useForm();
  const [login, loginStatus] =
    sharedLib.sharedApi.authentication.useLoginMutation();
  const [mobileLoginData, setMobileLoginData] = useState<MobileLoginData>();
  const storeMobileDeviceData = useSelector(selectMobileDeviceData);
  const [oauthUser, oauthUserStatus] =
    sharedLib.sharedApi.authentication.useOauthUserMutation();
  const [rememberMe, setRememberMe] = useState(() => {
    return localStorage?.getItem(LOCAL_STORAGE_REMEMBER_ME_KEY) === "true";
  });
  const dispatch = useDispatch();

  const query = useQuery();
  const { appendQuery } = useHistoryWithQuery();
  const connCodeRef = useRef<string>(query?.get("conn_code"));
  const oauthPlatformRef = useRef<string>(query?.get("oauth_platform"));
  const email = AntForm.useWatch(FormInput.EMAIL, form);
  const { isExternalConnectAccount } =
    useContext(ExternalConnectAccountContext) || {};
  const { isWhitelabel } = useBranding();
  const { t } = useTypedTranslation(LanguageNameSpaces.AUTH);
  const { isNewLook } = useTheme();
  const {
    display: displayResultMessage,
    error: inlineError,
    clearError,
  } = useAuthResultMessage();

  const loginErrorMessage = t("login.failed");

  useEffect(() => {
    if (email) {
      setMobileLoginData((loginData) => {
        if (!loginData) return;
        return { ...loginData, credentials: undefined };
      });
    }
  }, [email]);

  const handleRememberMeChange = (rememberMe: boolean) => {
    setRememberMe(rememberMe);
    localStorage?.setItem(
      LOCAL_STORAGE_REMEMBER_ME_KEY,
      rememberMe ? "true" : "false"
    );
  };

  const checkBiometricLogin = async () => {
    if (!IS_WEB_VIEW) {
      return;
    }
    try {
      const mobileLoginData = (await requestBiometricLogin()) || {};
      setMobileLoginData(mobileLoginData);
      const { credentials, device_data } = mobileLoginData;
      dispatch(
        sharedLib.slices.common.actions.setMobileDeviceData(device_data)
      );
      if (credentials?.email && credentials?.password) {
        handleRememberMeChange(!!credentials?.remember_me);
        onFinish();
      }
    } catch {}
  };

  useEffect(() => {
    if (!connCodeRef.current) {
      checkBiometricLogin().catch(() => {});
    }
  }, []);

  const handleLogin = async (captcha_token: string) => {
    const utmUuid = await getUtmUserUuid?.();
    if (!connCodeRef.current) {
      if (captcha_token) {
        const baseFormValues = {
          ...form.getFieldsValue(),
          ...mobileLoginData?.credentials,
          ...mobileLoginData?.device_data,
          remember_me: rememberMe,
        };
        const loginRequest: LoginRequest = {
          ...baseFormValues,
          captcha_token: captcha_token,
          is_whitelabel: isWhitelabel,
          extra_validations: false,
          utm_user_uuid: utmUuid,
        };
        try {
          const response: LoginResponse = await displayResultMessage(
            login(loginRequest).unwrap(),
            undefined,
            loginErrorMessage
          );
          if (!response.token) {
            setLoginResponse(response);
          } else {
            onFinalizedLogin();
            if (
              !mobileLoginData?.credentials?.email ||
              !mobileLoginData?.credentials?.password
            ) {
              triggerSaveBiometric(baseFormValues).catch(() => {});
            }
          }
        } catch (error) {
          if (
            isBiometricLogin &&
            error &&
            typeof error === "object" &&
            "status" in error &&
            error.status === 403
          ) {
            triggerClearBiometric(baseFormValues.email).catch(() => {});
          }
          setMobileLoginData(undefined);
        }
      }
    } else if (
      oauthPlatformRef.current &&
      connCodeRef.current &&
      captcha_token
    ) {
      displayResultMessage(
        oauthUser({
          ...storeMobileDeviceData,
          code: connCodeRef.current,
          captcha_token: captcha_token,
          is_whitelabel: isWhitelabel,
          oauth_platform: parseInt(oauthPlatformRef.current),
          utm_user_uuid: utmUuid,
          remember_me: rememberMe,
        }).unwrap(),
        undefined,
        loginErrorMessage
      )
        .then(async (result: LoginResponseWithUserId) => {
          if (!result.token) {
            setLoginResponse(result);
          } else {
            onFinalizedLogin();
          }
          if (result?.user_id) {
            handleUserTempOAuthData(result.user_id);
          }
        })
        .catch(() => {});
    }
  };

  const {
    verify: verifyCaptcha,
    isLoading: isCaptchaLoading,
    captcha,
  } = useCaptcha({
    onSuccess: (captcha_token) => {
      handleLogin(captcha_token).catch(() => {});
    },
    skipIdleRefetch: !!connCodeRef.current,
  });

  useEffect(() => {
    if (oauthPlatformRef.current && connCodeRef.current) {
      appendQuery(undefined, ["conn_code", "oauth_platform"]);
    }
  }, []);

  const isLoadingLogin = loginStatus.isLoading || oauthUserStatus.isLoading;
  const isSuccess = loginStatus.isSuccess || oauthUserStatus.isSuccess;
  const originalArgs = loginStatus.originalArgs || oauthUserStatus.originalArgs;
  const isBiometricLogin =
    !!mobileLoginData?.credentials?.email &&
    !!mobileLoginData?.credentials?.password;

  useEffect(() => {
    if (isSuccess && originalArgs) {
      onSuccessfulLogin(originalArgs);
      setIsBiometricLogin(
        !!mobileLoginData?.credentials?.email &&
          !!mobileLoginData?.credentials?.password
      );
    }
  }, [isSuccess]);

  const isLoading = isLoadingLogin || isCaptchaLoading;

  const onFinish = () => {
    verifyCaptcha();
  };

  useEffect(() => {
    if (connCodeRef.current && oauthPlatformRef.current) {
      onFinish();
    }
  }, []);

  const getTitle = () => {
    if (isExternalConnectAccount) {
      return <LoginDescription />;
    }
    if (isNewLook) {
      return (
        <PrimaryTitle $isMobile={isMobile}>
          {t("login.title_new_look")}
        </PrimaryTitle>
      );
    }
    return (
      <PrimaryTitle $isMobile={isMobile}>
        {t("login.welcome")}
        <br />
        {t("login.title")}
      </PrimaryTitle>
    );
  };

  return (
    <Form
      name="login"
      form={form}
      initialValues={{
        remember: true,
      }}
      onFinish={onFinish}
      onValuesChange={clearError}
      layout="vertical"
      requiredMark={false}
    >
      {getTitle()}
      <Form.Item
        label={t("fields.email")}
        name={FormInput.EMAIL}
        rules={getEmailFormItemRules(t)}
      >
        <Input
          size="large"
          data-testid={AUTH_LOGIN_EMAIL_INPUT_ID}
          {...EMAIL_INPUT_PROPS}
          autoComplete="username"
        />
      </Form.Item>
      <Form.Item
        label={t("fields.password")}
        name={FormInput.PASSWORD}
        rules={[
          {
            required: true,
            message: t("validation.password_required"),
          },
        ]}
        style={{ marginBottom: 8 }}
      >
        <Input.Password
          size="large"
          data-testid={AUTH_LOGIN_PASSWORD_INPUT_ID}
          autoComplete="current-password"
        />
      </Form.Item>
      {isNewLook ? (
        <RememberRow>
          <Checkbox
            checked={rememberMe}
            onChange={(event) => handleRememberMeChange(event.target.checked)}
            $size={18}
          >
            {t("login.remember_me")}
          </Checkbox>
          <Link
            onClick={() =>
              appendQuery(undefined, undefined, undefined, RESET_PASSWORD_PATH)
            }
            data-testid={AUTH_FORGOT_PASSWORD_LINK_ID}
          >
            {t("footer.forgot_password_new_look")}
          </Link>
        </RememberRow>
      ) : (
        <Flex
          style={{ marginTop: 12 }}
          onClick={() => handleRememberMeChange(!rememberMe)}
        >
          <Text>{t("login.remember_me")}</Text>
          <Checkbox checked={rememberMe} $size={24} />
        </Flex>
      )}
      <AuthInlineError message={inlineError} style={{ margin: "16px 0 0" }} />
      <AuthFormFooter
        buttonLoading={isLoading}
        buttonStyle={{ marginTop: isNewLook ? (inlineError ? 16 : 24) : 20 }}
        hideForgotPassword={isNewLook}
      />
      <SocialLogins />
      {captcha}
    </Form>
  );
};

export default LoginForm;

enum FormInput {
  EMAIL = "email",
  PASSWORD = "password",
}

const RememberRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-top: 16px;
  font-size: 14px;
  line-height: 20px;
  .ant-checkbox-wrapper {
    color: ${(p) => p.theme.colors.primaryText};
    font-size: 14px;
  }
  /* When the row wraps (narrow phones), the link stays at the end. */
  > a {
    margin-inline-start: auto;
  }
  /* Phones: a thumb-sized hit area without moving anything. */
  @media (max-width: ${LARGE_MOBILE}) {
    .ant-checkbox-wrapper,
    > a {
      padding: 10px 0;
      margin-block: -10px;
    }
  }
`;
